import React, { useState, useEffect } from 'react';
import type { PredictiveSettings } from '@/services/predictiveCalendarService';
import { predictiveCalendarService } from '@/services/predictiveCalendarService';

export const ConfigPredictiveCalendar: React.FC = () => {
  const [settings, setSettings] = useState<PredictiveSettings>({
    enabled: true,
    selected_regions: ['AM', 'PA', 'TO', 'RO', 'RR', 'AC', 'BR'],
    active_categories: [
      'Varejo & Compras',
      'Turismo & Viagens',
      'Esportes (Onde Assistir)',
      'Fiscal & Serviços',
      'Saúde & Clima',
    ],
    affiliate_amazon_tag: '',
    affiliate_shopee_id: '',
    affiliate_booking_url: '',
    affiliate_decolar_url: '',
    lead_time_sensitivity: 'padrao',
    auto_create_kanban_drafts: false,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null,
  );

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await predictiveCalendarService.getSettings();
      setSettings(data);
    } catch {
      setStatusMsg({
        type: 'error',
        text: 'Falha ao carregar configurações do calendário preditivo.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatusMsg(null);
    try {
      const res = await predictiveCalendarService.updateSettings(settings);
      setSettings(res.settings);
      setStatusMsg({
        type: 'success',
        text: 'Configurações do motor preditivo salvas com sucesso!',
      });
      setTimeout(() => setStatusMsg(null), 4000);
    } catch (err) {
      setStatusMsg({
        type: 'error',
        text: err instanceof Error && err.message ? err.message : 'Erro ao salvar configurações.',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (cat: string) => {
    const current = [...settings.active_categories];
    const idx = current.indexOf(cat);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(cat);
    }
    setSettings({ ...settings, active_categories: current });
  };

  const allCategories = [
    'Varejo & Compras',
    'Turismo & Viagens',
    'Esportes (Onde Assistir)',
    'Fiscal & Serviços',
    'Saúde & Clima',
    'Gastronomia Sazonal',
  ];

  if (loading) {
    return (
      <div style={{ padding: '40px', color: 'var(--text-3)' }}>Carregando configurações...</div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', color: 'var(--text-1)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>
            Configuração do Calendário Preditivo
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--text-3)', margin: '4px 0 0 0' }}>
            Gerencie o comportamento do motor estatístico, praças atendidas e monetização por
            afiliados.
          </p>
        </div>
        <a
          href="/inteligencia/calendario"
          style={{
            padding: '8px 14px',
            backgroundColor: 'var(--border-1)',
            color: 'var(--text-1)',
            borderRadius: '6px',
            textDecoration: 'none',
            fontSize: '12.5px',
            fontWeight: 600,
          }}
        >
          ← Voltar ao Radar
        </a>
      </div>

      {statusMsg && (
        <div
          style={{
            backgroundColor:
              statusMsg.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${statusMsg.type === 'success' ? 'var(--sig-ok)' : 'var(--sig-crit)'}`,
            color: statusMsg.type === 'success' ? 'var(--sig-ok)' : 'var(--sig-crit)',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '20px',
          }}
        >
          {statusMsg.text}
        </div>
      )}

      <form onSubmit={handleSave}>
        {/* Análise de lacunas (item 63): parte do que esta tela grava nenhum
            motor lê ainda. O que É lido: regiões e categorias (filtram o
            radar). O que NÃO é: tags de afiliado (o gerador usa URLs fixas,
            predictive_calendar.py:163-167). Dizer isso aqui evita o operador
            configurar comissão que não existe. */}
        <p
          role="status"
          style={{
            margin: '0 0 16px',
            padding: '10px 14px',
            backgroundColor: 'var(--bg-2)',
            borderLeft: '2px solid var(--sig-warn)',
            borderRadius: '6px',
            color: 'var(--text-3)',
            fontSize: '12.5px',
            lineHeight: 1.5,
          }}
        >
          Esta tela grava preferências que o radar ainda NÃO lê — regiões, categorias e afiliados
          não mudam o resultado hoje (em construção). O filtro real do radar é o seletor de mês/UF
          da própria tela do radar.
        </p>
        {/* 1. Ativação Geral */}
        <div
          style={{
            backgroundColor: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>
                Status do Motor Preditivo
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '4px 0 0 0' }}>
                Habilita ou desabilita as sugestões automáticas de pautas preditivas.
              </p>
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '13px',
              }}
            >
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span>{settings.enabled ? 'ATIVADO' : 'DESATIVADO'}</span>
            </label>
          </div>
        </div>

        {/* 2. Escopo Geográfico — Regra Mãe: Brasil Nacional */}
        <div
          style={{
            backgroundColor: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0' }}>
            Escopo Geográfico dos Dados (Regra Mãe)
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '0 0 12px 0' }}>
            Todas as análises temporais, dados de mercado, faturamento e volumes operam em nível
            nacional consolidado:
          </p>

          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(37, 99, 235, 0.12)',
              border: '1px solid var(--nexus-brand)',
              borderRadius: '6px',
              fontSize: '13px',
              color: 'var(--nexus-brand-hover)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <span style={{ fontSize: '18px' }}>🌎</span>
            <div>
              <strong>Brasil (Total Nacional — GEO=BR)</strong>
              <div style={{ fontSize: '11.5px', color: 'var(--text-2)', marginTop: '2px' }}>
                Dados agregados de todo o país (IBGE, ANAC, CNC, Ebit/NielsenIQ, MTur e Google
                Trends).
              </div>
            </div>
          </div>
        </div>

        {/* 3. Editorias Cobertas */}
        <div
          style={{
            backgroundColor: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0' }}>
            Editorias de Antecipação Ativas
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '0 0 16px 0' }}>
            Filtre quais esteiras de automação têm permissão para gerar rascunhos:
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '10px',
            }}
          >
            {allCategories.map((cat) => {
              const checked = settings.active_categories.includes(cat);
              return (
                <label
                  key={cat}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    backgroundColor: checked ? 'rgba(16, 185, 129, 0.12)' : 'var(--bg-0)',
                    border: `1px solid ${checked ? 'var(--sig-ok)' : 'var(--border-1)'}`,
                    borderRadius: '6px',
                    fontSize: '12.5px',
                    cursor: 'pointer',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleCategory(cat)} />
                  <span>{cat}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* 4. Tags de Afiliados */}
        <div
          style={{
            backgroundColor: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0' }}>
            Configuração de Afiliados & Monetização
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--text-3)', margin: '0 0 16px 0' }}>
            Insira os seus identificadores de afiliado para que os links gerem comissão
            automaticamente:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--text-2)',
                  marginBottom: '6px',
                }}
              >
                Amazon Associates Tag (ex: seusite-20)
              </label>
              <input
                type="text"
                value={settings.affiliate_amazon_tag || ''}
                onChange={(e) => setSettings({ ...settings, affiliate_amazon_tag: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'var(--bg-0)',
                  border: '1px solid var(--border-2)',
                  borderRadius: '6px',
                  color: 'var(--text-1)',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--text-2)',
                  marginBottom: '6px',
                }}
              >
                ID de Afiliado Shopee
              </label>
              <input
                type="text"
                value={settings.affiliate_shopee_id || ''}
                onChange={(e) => setSettings({ ...settings, affiliate_shopee_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'var(--bg-0)',
                  border: '1px solid var(--border-2)',
                  borderRadius: '6px',
                  color: 'var(--text-1)',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--text-2)',
                  marginBottom: '6px',
                }}
              >
                Link / AID Booking.com
              </label>
              <input
                type="text"
                value={settings.affiliate_booking_url || ''}
                onChange={(e) =>
                  setSettings({ ...settings, affiliate_booking_url: e.target.value })
                }
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'var(--bg-0)',
                  border: '1px solid var(--border-2)',
                  borderRadius: '6px',
                  color: 'var(--text-1)',
                  fontSize: '13px',
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--text-2)',
                  marginBottom: '6px',
                }}
              >
                Link de Parceria Decolar / Passagens Promo
              </label>
              <input
                type="text"
                value={settings.affiliate_decolar_url || ''}
                onChange={(e) =>
                  setSettings({ ...settings, affiliate_decolar_url: e.target.value })
                }
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'var(--bg-0)',
                  border: '1px solid var(--border-2)',
                  borderRadius: '6px',
                  color: 'var(--text-1)',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>
        </div>

        {/* O bloco 'Sensibilidade do Lead Time' saiu: o motor (get_radar_items) não lê lead_time_sensitivity — era flag morta vestida de controle (análise de lacunas, item 75). */}
        {/* Botão de Salvar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '12px 24px',
              backgroundColor: 'var(--nexus-brand-active)',
              color: 'var(--text-1)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13.5px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {saving ? 'Salvando...' : '💾 Salvar Configurações'}
          </button>
        </div>
      </form>
    </div>
  );
};
